/**
 * The price list in the app: what every analysis costs in credits, from
 * Admin › Catalog › Analysis prices (analysis_prices, migration
 * 20261005100000_pricing_controls), with the discount the signed-in account
 * has now. One source for the pricing page, plan cards, wizards and picker,
 * so a price changed in Admin shows everywhere.
 *
 * Until the list arrives (and on the prerendered pages) the defaults below
 * are used: the prices the code had. AnalysisPricesLoader loads it once the
 * app runs in the browser, and again when the account changes.
 */
import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PriceKey =
  | "cro_page" | "cro_reaudit" | "content" | "technical" | "heatmap"
  | "competitor_page" | "competitor_synthesis" | "figma_design" | "figma_compare"
  | "funnel_base" | "funnel_page" | "agent_min" | "agent_max" | "quick_check"
  | "visitor_analytics" | "visitor_clarity" | "suggestion_mockup" | "live_heatmaps" | "ab_test";

export interface PriceDiscount {
  percent_off: number;
  label: string | null;
  ends_at: string;
  scope: "all" | "plan" | "account";
}

export interface PriceRow {
  key: PriceKey;
  family: string;
  label: string;
  unit: "page" | "run" | "base";
  /** Base price in credits. */
  credits: number;
  /** What the signed-in account pays now (the base when there is no discount). */
  effective: number;
  discount: PriceDiscount | null;
}

export type PriceList = Record<PriceKey, PriceRow>;

const DEFAULTS: Array<[PriceKey, string, string, PriceRow["unit"], number]> = [
  ["cro_page", "cro_page", "CRO Page Audit", "page", 15],
  ["cro_reaudit", "cro_page", "CRO Page Audit, the same page again", "page", 5],
  ["content", "content", "Content Audit", "page", 15],
  ["technical", "technical", "Technical Audit", "page", 15],
  ["heatmap", "heatmap", "AI Predictive Attention", "page", 8],
  ["competitor_page", "competitor", "Competitor Audit, each competitor page", "page", 15],
  ["competitor_synthesis", "competitor", "Competitor Audit, the comparison", "run", 0],
  ["figma_design", "figma_design", "Figma Design Audit", "run", 15],
  ["figma_compare", "figma_design", "Figma Design Audit, the comparison", "run", 0],
  ["funnel_base", "funnel", "Funnel Audit, per run", "base", 40],
  ["funnel_page", "funnel", "Funnel Audit, per page", "page", 15],
  ["agent_min", "user_agent", "User Agent, minimum", "run", 80],
  ["agent_max", "user_agent", "User Agent, maximum", "run", 250],
  ["quick_check", "quick_check", "Quick Check, after the free ones", "run", 1],
  ["visitor_analytics", "cro_page", "Visitor data: each analytics tool an audit reads", "run", 5],
  ["visitor_clarity", "cro_page", "Visitor data: Microsoft Clarity", "run", 8],
  ["suggestion_mockup", "cro_page", "Suggestion mockup on the site", "run", 4],
  ["live_heatmaps", "live_heatmaps", "Live heatmaps, one study", "run", 40],
  ["ab_test", "ab_testing", "A/B test, one test", "run", 50],
];

export const DEFAULT_PRICE_LIST: PriceList = Object.fromEntries(
  DEFAULTS.map(([key, family, label, unit, credits]) => [key, { key, family, label, unit, credits, effective: credits, discount: null }]),
) as PriceList;

let current: PriceList = DEFAULT_PRICE_LIST;
const listeners = new Set<() => void>();

function emit(next: PriceList) {
  current = next;
  listeners.forEach((l) => l());
}

/** Merge rows from analysis_prices_public into the list (unknown keys are ignored). */
export function applyPriceRows(rows: unknown): PriceList {
  if (!Array.isArray(rows)) return current;
  const next: PriceList = { ...DEFAULT_PRICE_LIST };
  for (const r of rows as Array<Partial<PriceRow>>) {
    const key = r?.key as PriceKey | undefined;
    if (!key || !(key in next)) continue;
    const credits = Number(r.credits);
    const effective = Number(r.effective);
    next[key] = {
      ...next[key],
      label: typeof r.label === "string" ? r.label : next[key].label,
      unit: (r.unit as PriceRow["unit"]) ?? next[key].unit,
      credits: Number.isFinite(credits) ? credits : next[key].credits,
      effective: Number.isFinite(effective) ? effective : Number.isFinite(credits) ? credits : next[key].effective,
      discount: r.discount && typeof r.discount === "object" ? (r.discount as PriceDiscount) : null,
    };
  }
  emit(next);
  return next;
}

let inflight: Promise<void> | null = null;
let loadedFor: string | null | undefined;

/** Load the list (with the account's discounts when it is given). Safe to call often. */
export function loadAnalysisPrices(accountId?: string | null): Promise<void> {
  const key = accountId ?? null;
  if (loadedFor === key && inflight) return inflight;
  loadedFor = key;
  inflight = (async () => {
    try {
      const { data, error } = await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>)(
        "analysis_prices_public", { p_account: key },
      );
      if (!error && data) applyPriceRows(data);
    } catch {
      /* the defaults stay */
    }
  })();
  return inflight;
}

/** Load again for the same account (after Admin changes a price or a discount). */
export function reloadAnalysisPrices(): Promise<void> {
  const key = loadedFor ?? null;
  loadedFor = undefined;
  inflight = null;
  return loadAnalysisPrices(key);
}

/** For tests: back to the defaults. */
export function resetAnalysisPrices() {
  loadedFor = undefined;
  inflight = null;
  emit(DEFAULT_PRICE_LIST);
}

/** The list as it is now (not reactive; components use useAnalysisPrices). */
export function getAnalysisPrices(): PriceList {
  return current;
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The price list, re-rendering when it loads or changes. The prerendered HTML uses the defaults. */
export function useAnalysisPrices(): PriceList {
  return useSyncExternalStore(subscribe, () => current, () => DEFAULT_PRICE_LIST);
}

/** What one CRO Page Audit costs the signed-in account now (after its discount). */
export function croAuditCost(list: PriceList = current): number {
  return Math.max(1, list.cro_page.effective || list.cro_page.credits);
}

/** "-30% until 15 Nov" for a discount, or null. */
export function discountLabel(d: PriceDiscount | null | undefined): string | null {
  if (!d) return null;
  const until = new Date(d.ends_at);
  const day = Number.isNaN(until.getTime()) ? "" : until.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `-${d.percent_off}%${day ? ` until ${day}` : ""}`;
}

/** A price after a discount factor, the way the server rounds it (never below 1). */
export function discounted(credits: number, row: PriceRow): number {
  if (credits <= 0) return 0;
  if (!row.discount) return credits;
  return Math.max(1, Math.ceil((credits * (100 - row.discount.percent_off)) / 100));
}
